import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const token = searchParams.get('token');
        
        if (!token) {
          setStatus('error');
          setMessage('Verification token not found in URL');
          return;
        }

        const response = await axios.post(
          `${API}/auth/verify-email`,
          null,
          { params: { token } }
        );

        if (response.status === 200) {
          setStatus('success');
          setMessage(response.data.message);
          
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        }
      } catch (error) {
        setStatus('error');
        const errorMessage = error.response?.data?.detail || 'Verification failed. The link may have expired.';
        setMessage(errorMessage);
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Card className="bg-white/95 backdrop-blur-md shadow-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-serif font-bold text-slate-900">
              Email Verification
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center space-y-4 py-8">
            {status === 'loading' && (
              <>
                <Loader2 className="h-16 w-16 animate-spin text-blue-500" />
                <p className="text-center text-slate-600">{message}</p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <CheckCircle2 className="h-16 w-16 text-green-500" />
                <p className="text-center text-green-600 font-medium text-lg">{message}</p>
                <p className="text-sm text-slate-500">Redirecting to login...</p>
              </>
            )}
            
            {status === 'error' && (
              <>
                <AlertCircle className="h-16 w-16 text-red-500" />
                <p className="text-center text-red-600 font-medium">{message}</p>
                <div className="flex space-x-2 w-full mt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/register')}
                    className="flex-1"
                  >
                    Register Again
                  </Button>
                  <Button 
                    onClick={() => navigate('/login')}
                    className="flex-1 bg-slate-900 hover:bg-slate-800"
                  >
                    Go to Login
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;